import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Filter, Paperclip, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { allMessageCategories, type MessageCategory, type MessageSummary } from "@/api/mailApi";
import { categoryTheme } from "./categoryTheme";

export interface MessageFilterState {
    categories: Set<MessageCategory>;
    /** true → only unread, false → only read, null → either. */
    unread: boolean | null;
    flagged: boolean;
    hasAttachments: boolean;
}

export const emptyFilter = (): MessageFilterState => ({
    categories: new Set<MessageCategory>(),
    unread: null,
    flagged: false,
    hasAttachments: false
});

interface MessageFiltersProps {
    messages: MessageSummary[];
    value: MessageFilterState;
    onChange: (next: MessageFilterState) => void;
}

/**
 * Gmail-style filter bar. Categories live behind a multi-select
 * checkbox dropdown (keeps the horizontal real-estate free); the
 * high-signal flags — unread, flagged, attachments — are toggle
 * buttons inline so one click filters. A "Clear" pill appears when
 * any filter is active.
 */
export function MessageFilters({ messages, value, onChange }: MessageFiltersProps) {
    const counts = new Map<MessageCategory, number>();
    let unread = 0, flagged = 0, withAttach = 0;
    for (const m of messages) {
        if (m.category) counts.set(m.category, (counts.get(m.category) ?? 0) + 1);
        if (!m.isRead) unread++;
        if (m.isFlagged) flagged++;
        if (m.hasAttachments) withAttach++;
    }

    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const close = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const activeCount =
        value.categories.size +
        (value.unread !== null ? 1 : 0) +
        (value.flagged ? 1 : 0) +
        (value.hasAttachments ? 1 : 0);

    const toggleCategory = (c: MessageCategory) => {
        const next = new Set(value.categories);
        if (next.has(c)) next.delete(c); else next.add(c);
        onChange({ ...value, categories: next });
    };

    return (
        <div className="flex items-center gap-1.5 border-b px-3 py-2" role="toolbar" aria-label="Filter messages">
            <div ref={wrapperRef} className="relative">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    className={cn(
                        "inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs",
                        value.categories.size > 0 ? "bg-primary/10 border-primary/40" : "bg-background hover:bg-accent/50"
                    )}
                >
                    <Filter className="h-3 w-3" />
                    Labels
                    {value.categories.size > 0 ? (
                        <span className="rounded-full bg-primary/20 px-1.5 text-[10px] font-semibold text-primary">
                            {value.categories.size}
                        </span>
                    ) : null}
                    <ChevronDown className="h-3 w-3" />
                </button>
                {open ? (
                    <ul
                        role="listbox"
                        aria-multiselectable="true"
                        aria-label="Categories"
                        className="absolute left-0 top-full z-20 mt-1 max-h-72 w-56 overflow-auto rounded-md border bg-popover p-1 text-sm shadow-lg"
                    >
                        {allMessageCategories.map((c) => {
                            const theme = categoryTheme[c];
                            const isChecked = value.categories.has(c);
                            const count = counts.get(c) ?? 0;
                            return (
                                <li key={c}>
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={isChecked}
                                        onClick={() => toggleCategory(c)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-accent"
                                    >
                                        <span className={cn(
                                            "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                            isChecked ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"
                                        )}>
                                            {isChecked ? <Check className="h-3 w-3" /> : null}
                                        </span>
                                        <span className={cn("h-2 w-2 shrink-0 rounded-full", theme.dot)} aria-hidden />
                                        <span className="flex-1 text-xs">{theme.label}</span>
                                        <span className="text-[10px] text-muted-foreground">{count}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>

            <ToggleFilter
                label="Unread"
                count={unread}
                active={value.unread === true}
                onClick={() => onChange({ ...value, unread: value.unread === true ? null : true })}
            />
            <ToggleFilter
                label="Flagged"
                count={flagged}
                active={value.flagged}
                icon={<Star className="h-3 w-3" />}
                onClick={() => onChange({ ...value, flagged: !value.flagged })}
            />
            <ToggleFilter
                label="Attachments"
                count={withAttach}
                active={value.hasAttachments}
                icon={<Paperclip className="h-3 w-3" />}
                onClick={() => onChange({ ...value, hasAttachments: !value.hasAttachments })}
            />

            {activeCount > 0 ? (
                <button
                    type="button"
                    onClick={() => onChange(emptyFilter())}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                >
                    Clear filters
                </button>
            ) : null}
        </div>
    );
}

function ToggleFilter({
    label, count, active, icon, onClick
}: {
    label: string;
    count: number;
    active: boolean;
    icon?: React.ReactNode;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={cn(
                "inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs",
                active ? "bg-primary/10 border-primary/40 text-primary" : "bg-background hover:bg-accent/50"
            )}
        >
            {icon}
            <span>{label}</span>
            <span className={active ? "text-primary" : "text-muted-foreground"}>{count}</span>
        </button>
    );
}

/** Does a message pass the current filter set? */
export function matchesFilter(m: MessageSummary, f: MessageFilterState): boolean {
    if (f.categories.size > 0 && (!m.category || !f.categories.has(m.category))) return false;
    if (f.unread === true && m.isRead) return false;
    if (f.unread === false && !m.isRead) return false;
    if (f.flagged && !m.isFlagged) return false;
    if (f.hasAttachments && !m.hasAttachments) return false;
    return true;
}
